import { addDays } from 'date-fns/addDays'
import { differenceInDays } from 'date-fns/differenceInDays'
import { format } from 'date-fns/format'
import { isValid } from 'date-fns/isValid'
import { parse } from 'date-fns/parse'
import { parseISO } from 'date-fns/parseISO'

const FORM_DATE_FORMAT = 'yyyyMMdd'
/** Leading calendar date in legacy/ISO recovery strings (ignores time and TZ offset). */
const CALENDAR_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})/

/** Converts legacy yyyy-MM-dd, ISO datetime, or yyyyMMdd to yyyyMMdd for form state. */
export function normalizeFormDateString(date: string | null | undefined): string {
  if (!date) return ''
  const trimmed = date.trim()
  if (/^\d{8}$/.test(trimmed)) {
    const parsed = parse(trimmed, FORM_DATE_FORMAT, new Date())
    return isValid(parsed) ? trimmed : ''
  }
  const calendarMatch = CALENDAR_DATE_PREFIX.exec(trimmed)
  const dashed = calendarMatch?.[1]
  if (dashed) {
    const parsed = parse(dashed, 'yyyy-MM-dd', new Date())
    if (isValid(parsed) && format(parsed, 'yyyy-MM-dd') === dashed) {
      return format(parsed, FORM_DATE_FORMAT)
    }
  }
  return ''
}

/** When both dates are missing/invalid, fall back to today and tomorrow in yyyyMMdd. */
export function resolveFormDatesWithDefaults(
  startDate: string,
  endDate: string,
  referenceDate: Date = new Date(),
) {
  const normalizedStartDate = normalizeFormDateString(startDate)
  const normalizedEndDate = normalizeFormDateString(endDate)

  if (!normalizedStartDate && !normalizedEndDate) {
    return {
      startDate: format(referenceDate, FORM_DATE_FORMAT),
      endDate: format(addDays(referenceDate, 1), FORM_DATE_FORMAT),
    }
  }

  return { startDate: normalizedStartDate, endDate: normalizedEndDate }
}

export type FormatDateParams = {
  dateStr: string | null
  dateFormat?: string
}

export const formatDate = ({ dateStr, dateFormat = 'MM/dd/yyyy' }: FormatDateParams) => {
  let formatedDate = ''
  try {
    if (dateStr) {
      formatedDate = format(parse(dateStr, 'yyyyMMdd', new Date()), dateFormat)
    }
  } catch {
    formatedDate = ''
  }

  return formatedDate
}

export const addFormattedDays = (dateStr: string, days: number, dateFormat = 'yyyyMMdd') => {
  if (dateStr) {
    return format(addDays(parse(dateStr, 'yyyyMMdd', new Date()), days), dateFormat)
  }
  return ''
}

const now = Date.now()
export const defaultToday = format(now, 'yyyyMMdd')
export const defaultTomorrow = format(addDays(now, 1), 'yyyyMMdd')
export const defaultMaxDate = format(addDays(now, 330), 'yyyyMMdd')

// Shared methods for Flights, SOPQ Flights and RC
/**
 * @param {object} param0
 * @param {string | null} [param0.departureDate]
 * @param {string | null} [param0.arrivalDate] - The name of the employee.
 * @param {boolean} isOneWay
 */
export const getDateRange = (
  { departureDate, arrivalDate }: { departureDate: string | null; arrivalDate: string | null },
  isOneWay: boolean,
) => {
  if (!departureDate) return ''
  const start = formatDate({ dateStr: departureDate })
  if (isOneWay) return start
  const end = formatDate({ dateStr: arrivalDate })
  return `${start} – ${end}`
}

export const getPackagesDateRange = ({
  startDate,
  endDate,
}: {
  startDate: string | null
  endDate: string | null
}) => {
  if (!startDate) return ''
  return `${formatDate({ dateStr: startDate })} - ${formatDate({ dateStr: endDate })}`
}

export const getHotelDateRange = ({
  startDate,
  endDate,
}: {
  startDate: string | null
  endDate: string | null
}) => {
  if (!startDate && !endDate) return ''
  const start = formatDate({ dateStr: startDate })
  const end = formatDate({ dateStr: endDate })
  return [start, end].join(' – ')
}

export const getRcMinDate = (pickupDate?: string) => {
  if (pickupDate) {
    return formatDate({ dateStr: pickupDate, dateFormat: 'yyyyMMdd' })
  }
  return format(new Date(), 'yyyyMMdd')
}

export const getTimeOptions = () => {
  const times = new Array(48).fill(null).map((_, i) => i)

  return times.map((time) => {
    const date = new Date(0, 0, 0, Math.floor(time / 2), time % 2 === 0 ? 0 : 30)
    const value = format(date, 'HH:mm')
    let label = format(date, 'h:mm a')
    if (value === '00:00') {
      label = 'Midnight'
    } else if (value === '12:00') {
      label = 'Noon'
    }
    return (
      <option key={value} value={value}>
        {label}
      </option>
    )
  })
}

export function formatPackagesDateFromSearchLink(url: string) {
  function parseDateFromPkgURL(dateMatch: RegExpMatchArray | null) {
    if (!dateMatch) return null
    return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`
  }

  const departureDateMatch = url.match(/departure-date=(\d{4})(\d{2})(\d{2})/)
  const returnDateMatch = url.match(/return-date=(\d{4})(\d{2})(\d{2})/)
  const departureDateISO = parseDateFromPkgURL(departureDateMatch)
  const returnDateISO = parseDateFromPkgURL(returnDateMatch)
  if (!departureDateMatch || !returnDateMatch || !departureDateISO || !returnDateISO) {
    return {
      formattedDepartureDate: '',
      formattedReturnDate: '',
      daysBetween: 0,
    }
  }
  const formattedDepartureDate = format(parseISO(departureDateISO), 'MMM do')
  const formattedReturnDate = format(parseISO(returnDateISO), 'MMM do')

  const daysBetween = differenceInDays(parseISO(returnDateISO), parseISO(departureDateISO))

  return { formattedDepartureDate, formattedReturnDate, daysBetween }
}
